import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./LostFound.css";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import { BeatLoader, ClipLoader } from "react-spinners";
import LFload from "../Loading/lostfoundload";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
const PAGE_SIZE = 10; // match backend or pass &limit
const TABS = ["lost", "found", "my"];

export default function LostFound() {
  // below other useState(...)
  const [isSubmitting, setIsSubmitting] = useState(false); // create/edit modal
  const [deletingId, setDeletingId] = useState(null); // which item is deleting (id or null)

  const [activeTab, setActiveTab] = useState("lost");
  const queryClient = useQueryClient();

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [editId, setEditId] = useState(null);
  // form
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemLocation, setItemLocation] = useState("");
  const [itemType, setItemType] = useState("lost");

  // read-more
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // 🔧 kebab menu state + refs map (fix)
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRefs = useRef(new Map()); // id -> HTMLElement

  const listRef = useRef(null);
  const observer = useRef(null);

  const getAuth = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["lostfound", activeTab],
      queryFn: async ({ pageParam = 1 }) => {
        const res = await axios.get(
          `${process.env.REACT_APP_SERVER}/api/lostfound?filter=${activeTab}&page=${pageParam}&limit=${PAGE_SIZE}`,
          getAuth(),
        );

        return res.data;
      },
      getNextPageParam: (lastPage, pages) => {
        const items = Array.isArray(lastPage?.items) ? lastPage.items : [];
        return items.length < PAGE_SIZE ? undefined : pages.length + 1;
      },
      staleTime: 1000 * 60 * 5,
    });
  const items = data?.pages.flatMap((p) => p.items) || [];

  const fmt = (ts) =>
    ts
      ? new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
      : "—";

  // 🔧 outside click that respects per-item refs
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!menuOpenId) return;
      const node = menuRefs.current.get(menuOpenId);
      if (!node) {
        setMenuOpenId(null);
        return;
      }
      if (!node.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [menuOpenId]);

  const lastItemRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        },
        { root: listRef.current || null, rootMargin: "400px", threshold: 0 },
      );

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage],
  );

  // modal open helpers
  const openCreate = () => {
    setModalMode("create");
    setEditId(null);
    setItemName("");
    setItemDescription("");
    setItemLocation("");
    setItemType("lost");
    setShowModal(true);
  };

  const openEdit = (it) => {
    setModalMode("edit");
    setEditId(it.id);
    setItemName(it.item_name || "");
    setItemDescription(it.item_description || "");
    setItemLocation(it.item_location || "");
    setItemType(it.status || "lost");
    setShowModal(true);
  };

  const handleDelete = async (it) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      setDeletingId(it.id);
      await axios.delete(
        `${process.env.REACT_APP_SERVER}/api/lostfound/${it.id}`,
        getAuth(),
      );

      queryClient.setQueriesData({ queryKey: ["lostfound"] }, (old) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((x) => x.id !== it.id),
          })),
        };
      });
    } catch (e) {
      console.error("delete failed:", e);
      alert("Failed to delete item.");
    } finally {
      setMenuOpenId(null);
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      item_name: itemName.trim(),
      item_description: itemDescription.trim(),
      item_location: itemLocation.trim(),
      status: itemType,
    };
    if (!body.item_name || !body.item_description || !body.item_location)
      return;

    try {
      setIsSubmitting(true);

      if (modalMode === "create") {
        const res = await axios.post(
          `${process.env.REACT_APP_SERVER}/api/lostfound`,
          body,
          getAuth(),
        );
        const newItem = res?.data?.item;

        queryClient.invalidateQueries(["lostfound"]);
      } else {
        const res = await axios.put(
          `${process.env.REACT_APP_SERVER}/api/lostfound/${editId}`,
          body,
          getAuth(),
        );
        const updated = res?.data?.item;
        if (updated) {
          queryClient.setQueriesData({ queryKey: ["lostfound"] }, (old) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((x) =>
                  x.id === updated.id ? { ...x, ...updated } : x,
                ),
              })),
            };
          });
        }
      }

      setShowModal(false);
      setMenuOpenId(null);
      setModalMode("create");
      setEditId(null);
      setItemName("");
      setItemDescription("");
      setItemLocation("");
      setItemType("lost");
    } catch (e) {
      console.error("submit failed:", e);
      alert("Failed to save item.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="homecontainer-3">
      <div className="homecontainer-3-1">
        <h3>Lost and Found</h3>
        <br />
        <div className="tabs-container">
          {TABS.map((t) => (
            <button
              key={t}
              className={`switch-btn ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t === "my" ? "My Posts" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <button className="post-btn" onClick={openCreate}>
            Report
          </button>
        </div>

        <div className="items-list" ref={listRef}>
          {items.length === 0 && !isLoading && (
            <div className="item-card-nope">No items listed</div>
          )}

          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            const isOpen = expanded.has(item.id);
            const showReadMore = (item.item_description || "").length > 160;

            const CardContent = () => (
              <>
                {/* Top line: reporter + kebab */}
                <div className="meta-line">
                  <div className={`status-pill status-${item.status}`}>
                    {item.status}
                  </div>
                  <span className="meta-value">
                    {item.resolved_at && <FaCheckCircle />}
                  </span>
                </div>
                <div className="reporter-line">
                  {item.reporter_profile_url ? (
                    <img
                      className="reporter-avatar"
                      src={item.reporter_profile_url}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/avatar.jpg";
                      }}
                    />
                  ) : (
                    <img className="reporter-avatar" src="/avatar.jpg" alt="" />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="reporter-name">
                      {item.reporter_name || "—"}
                    </div>
                    <div className="reporter-name">{item.reporter_course}</div>
                  </div>

                  {/* Kebab menu for "my" tab */}
                  {activeTab === "my" && (
                    <div
                      className="kebab-wrap"
                      ref={(el) => {
                        if (el) menuRefs.current.set(item.id, el);
                        else menuRefs.current.delete(item.id);
                      }}
                      style={{ position: "relative" }}
                    >
                      <button
                        type="button"
                        className="kebab-btn"
                        aria-label="More options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenId((s) =>
                            s === item.id ? null : item.id,
                          );
                        }}
                        disabled={deletingId === item.id}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: 6,
                          cursor:
                            deletingId === item.id ? "not-allowed" : "pointer",
                          borderRadius: 8,
                        }}
                        title="More options"
                      >
                        <BsThreeDotsVertical size={18} />
                      </button>

                      {menuOpenId === item.id && (
                        <div
                          className="kebab-menu"
                          style={{
                            position: "absolute",
                            top: "100%",
                            right: 0,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
                            minWidth: 160,
                            zIndex: 30, // ensure above card
                            overflow: "hidden",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="kebab-item"
                            onClick={() => openEdit(item)}
                            style={menuItemStyle}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="kebab-item"
                            onClick={() => handleDelete(item)}
                            disabled={deletingId === item.id}
                            style={{
                              ...menuItemStyle,
                              color: "#b91c1c",
                              opacity: deletingId === item.id ? 0.6 : 1,
                              cursor:
                                deletingId === item.id
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            {deletingId === item.id ? (
                              <span
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  alignItems: "center",
                                }}
                              >
                                Deleting
                                <ClipLoader color="#db1212" size={13} />
                              </span>
                            ) : (
                              "Delete"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Title */}
                <p className="item-title">{item.item_name}</p>
                {/* Description */}
                <div className={`item-desc ${isOpen ? "open" : ""}`}>
                  {item.item_description}
                </div>
                {showReadMore && (
                  <button
                    type="button"
                    className="readmore-link"
                    onClick={() => toggleExpand(item.id)}
                  >
                    {isOpen ? "Read less" : "Read more"}
                  </button>
                )}
                {/* Status + resolved */}
                <div className="report-contianer">
                  <span className="meta-label">{fmt(item.reported_at)}</span>
                </div>
              </>
            );

            return isLast ? (
              <div className="item-card" key={item.id} ref={lastItemRef}>
                <CardContent />
              </div>
            ) : (
              <div className="item-card" key={item.id}>
                <CardContent />
              </div>
            );
          })}

          {isFetchingNextPage && <LFload />}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-lf" onClick={(e) => e.stopPropagation()}>
              <h3>
                {modalMode === "create" ? "Post Lost/Found Item" : "Edit Item"}
              </h3>
              <form onSubmit={handleSubmit}>
                <label>
                  Item Name
                  <input
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Location
                  <input
                    value={itemLocation}
                    onChange={(e) => setItemLocation(e.target.value)}
                    required
                  />
                </label>
                <label>
                  Type
                  <select
                    value={itemType}
                    onChange={(e) => setItemType(e.target.value)}
                  >
                    <option value="lost">Lost</option>
                    <option value="found">Found</option>
                  </select>
                </label>
                <div className="modal-buttons">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={isSubmitting ? "btn-loading" : ""}
                    aria-live="polite"
                  >
                    {isSubmitting
                      ? modalMode === "create" && (
                          <BeatLoader color="#ffffff" size={10} />
                        )
                      : modalMode === "create"
                        ? "Submit"
                        : "Save"}
                  </button>
                  <button type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const menuItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "10px 12px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.92rem",
};
